import { Modal } from 'react-bootstrap'
import DateComp from '../../../components/dateComponent/DateComp'
import DateTimeComp from '../../../components/dateComponent/DateTimeComp'
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp'
import { LoadingComponent } from '../../../components/loadingComponent/LoadingComponent'
import CreditCardRequestStatusComp from '../../../components/StatusComp/CreditCardRequestStatusComp'
import { useGetCreditCardRequestedUserDetailQuery } from '../../../services/authApi'

type Props = {
    show: boolean
    handleClose: () => void
    orgUserCreditCardRequestId: number | null
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className='col-md-4'>
        <label className='fw-semibold text-muted'>{label}</label>
        <div>{children}</div>
    </div>
)

const CreditCardRequestUserDetailModal = ({ show, handleClose, orgUserCreditCardRequestId }: Props) => {
    const { data, isFetching } = useGetCreditCardRequestedUserDetailQuery(orgUserCreditCardRequestId!, {
        skip: !show || !orgUserCreditCardRequestId,
    })

    const detail = data?.result
    const fullName = [detail?.firstName, detail?.middleName, detail?.lastName].filter(Boolean).join(' ')

    const shippingAddress =
        detail?.shipToAddress
        || detail?.findAddress
        || [
            detail?.addressLine1,
            detail?.addressLine2,
            detail?.city,
            detail?.state,
            detail?.postalCode,
        ].filter(Boolean).join(', ')
        || ''

    return (
        <Modal
            className='modal fade'
            id='kt_modal_cc_request_user_detail'
            tabIndex={-1}
            dialogClassName='modal-dialog modal-dialog-centered mw-950px'
            show={show}
            onHide={handleClose}
        >
            <div className='modal-header'>
                <h2 className='fw-bolder'>Corporate Credit Card Request Detail</h2>
                <div className='btn btn-icon btn-sm btn-active-icon-primary' onClick={handleClose}>
                    <i className='fas fa-times'></i>
                </div>
            </div>
            <div className='modal-body scroll-y mx-lg-5 my-4'>
                {isFetching && <LoadingComponent />}
                {!isFetching && detail && (
                    <div className='row g-5'>
                        <div className='col-12'>
                            <div className='d-flex align-items-center mb-4'>
                                <div className='symbol symbol-circle symbol-50px overflow-hidden me-3'>
                                    <div className='symbol-label fs-3 bg-light-info text-info text-capitalize'>
                                        {detail?.firstName?.charAt(0) || '?'}
                                    </div>
                                </div>
                                <div>
                                    <div className='fw-bold text-gray-800 text-capitalize'>{fullName || 'N/A'}</div>
                                    <div className='text-muted text-lowercase'>{detail?.email || 'N/A'}</div>
                                </div>
                            </div>
                        </div>

                        <Field label='Employee Id'>{detail?.employeeId || 'N/A'}</Field>
                        <Field label='Cell Phone'>{detail?.cellPhone || 'N/A'}</Field>
                        <Field label='Department'>{detail?.department?.title || 'N/A'}</Field>

                        <Field label='Designation'>{detail?.designation?.title || 'N/A'}</Field>
                        <Field label='Division'>{detail?.division?.title || 'N/A'}</Field>
                        <Field label='User Type'>{detail?.userType?.value || 'N/A'}</Field>

                        <Field label='Allowed Expense Type'>{detail?.expenseType?.value || 'N/A'}</Field>
                        <Field label='Office Location'>{detail?.location?.value || 'N/A'}</Field>
                        <Field label='Manager'>
                            <div>{detail?.managerName || 'N/A'}</div>
                            {detail?.managerEmail && (
                                <div className='text-muted text-lowercase'>{detail.managerEmail}</div>
                            )}
                        </Field>
                        <Field label='CC Requested Date'>
                            <DateComp formattedDate={detail?.ccRequestedDate} />
                        </Field>
                        <Field label='Corp CC Limit'>
                            {detail?.spendingLimit != null && Number(detail.spendingLimit) > 0
                                ? <CurrencyComp amount={detail.spendingLimit} />
                                : 'N/A'}
                        </Field>
                        <Field label='Corp CC Status'>
                            <CreditCardRequestStatusComp
                                statusId={detail?.creditCardCardRequest?.id}
                                statusTitle={detail?.creditCardCardRequest?.title}
                            />
                        </Field>


                        <div className='col-12'>
                            <div className='separator separator-dashed my-2'></div>
                            <h5 className='fw-bold mb-3'>Corp CC Shipping Details</h5>
                        </div>
                        <div className='col-md-8'>
                            <label className='fw-semibold text-muted'>Shipping Address (Find Address)</label>
                            <div>{shippingAddress || 'N/A'}</div>
                        </div>
                        <Field label='Address Line 1'>{detail?.addressLine1 || 'N/A'}</Field>
                        <Field label='Address Line 2'>{detail?.addressLine2 || 'N/A'}</Field>
                        <Field label='City'>{detail?.city || 'N/A'}</Field>
                        <Field label='State'>{detail?.state || 'N/A'}</Field>
                        <Field label='Postal Code'>{detail?.postalCode || 'N/A'}</Field>

                        <div className='col-12'>
                            <div className='separator separator-dashed my-2'></div>
                            <h5 className='fw-bold mb-3'>Approval Details</h5>
                        </div>
                        <Field label='Corp CC Approver'>
                            <div className='text-capitalize'>{detail?.ccAssignedApproverName || 'N/A'}</div>
                            {detail?.ccAssignedApproverEmail && (
                                <div className='text-muted text-lowercase'>{detail.ccAssignedApproverEmail}</div>
                            )}
                        </Field>
                        <Field label='Approved Date'>
                            <DateComp formattedDate={detail?.ccApprovedDate} />
                        </Field>
                        <Field label='Approved By'>
                            <div className='text-capitalize'>{detail?.ccApprovedByUserName || 'N/A'}</div>
                            {detail?.ccApprovedByUserEmail && (
                                <div className='text-muted text-lowercase'>{detail.ccApprovedByUserEmail}</div>
                            )}
                        </Field>

                        <Field label='Rejected Date'>
                            <DateComp formattedDate={detail?.ccRejectedDate} />
                        </Field>
                        <Field label='Rejected By'>
                            <div className='text-capitalize'>{detail?.ccRejectedByUserName || 'N/A'}</div>
                            {detail?.ccRejectedByUserEmail && (
                                <div className='text-muted text-lowercase'>{detail.ccRejectedByUserEmail}</div>
                            )}
                        </Field>
                        <Field label='Ordered Date'>
                            <DateComp formattedDate={detail?.ccOrderedDate} />
                        </Field>

                        <Field label='Modified On'>
                            <DateTimeComp formattedDate={detail?.ccRequestModifiedOn} />
                        </Field>
                        <Field label='Modified By'>
                            <div className='text-capitalize'>{detail?.ccRequestModifiedBy || 'N/A'}</div>
                        </Field>
                        <div className='col-md-4'>
                            <label className='fw-semibold text-muted'>Comments</label>
                            <div>{detail?.comments || 'N/A'}</div>
                        </div>
                    </div>
                )}
                {!isFetching && !detail && (
                    <div className='text-muted'>No detail found for this request.</div>
                )}
            </div>
            <div className='modal-footer'>
                <button type='button' className='btn btn-light' onClick={handleClose}>
                    Close
                </button>
            </div>
        </Modal>
    )
}

export default CreditCardRequestUserDetailModal
